import React, { useCallback, useState } from 'react';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';

import { useGetLinksQuery } from '../../../hooks/customerApi';
import { useCustomerApiContext } from '../../../hooks/useCustomerApiContext';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useHasWorkspacePermission } from '../../../hooks/useHasWorkspacePermission';
import { getResultOrError } from '../../../utils/errors';
import { LinksTable } from '../../link/LinksTable';
import { TableLoadError } from '../../TableLoadError/TableLoadError';
import { IntegrationSettingsLayout } from './IntegrationSettingsLayout';
import { OnboardLinkModal } from './OnboardLinkModal';

export const IntegrationsMainView = () => {
  const { context } = useCustomerApiContext();
  const [{ data, error, fetching }, reExecuteQuery] = useGetLinksQuery({
    context,
    pause: !context.token,
  });
  const { isError, result: links } = getResultOrError(data?.links, error);
  const canAddLink = useHasWorkspacePermission(Permissions.link.create);
  const refresh = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  const [isCreatingLink, setIsCreatingLink] = useState<boolean>(false);
  useDocumentTitle('Links');
  const toggleCreateLinkModal = useCallback(
    () => setIsCreatingLink((val) => !val),
    []
  );

  if (isError) {
    return (
      <IntegrationSettingsLayout>
        <TableLoadError
          entityColumnName="Link"
          rightColumnName="ID"
          {...{ error, retry: refresh, fetching }}
        />
      </IntegrationSettingsLayout>
    );
  }
  return (
    <>
      <IntegrationSettingsLayout
        summarySectionActions={
          canAddLink ? (
            <PrimaryButtonV2
              data-testid="add-link-button"
              onClick={toggleCreateLinkModal}
              secondaryIcon="+"
            >
              Add
            </PrimaryButtonV2>
          ) : undefined
        }
      >
        <LinksTable data={links.nodes} />
        {isCreatingLink && (
          <OnboardLinkModal refresh={refresh} onClose={toggleCreateLinkModal} />
        )}
      </IntegrationSettingsLayout>
    </>
  );
};
